<template>
  <div class="d-flex mt-1 mb-2 justify-content-center">
    <div class="input-wrapper" :class="{ fill, error, disabled }">
      <div class="placeholder-container" @click="onClick">
        <div v-if="value === ''" class="placeholder-text">
          {{ placeholder }}
        </div>
        <div v-else class="selected-option">{{ selectedValue }}</div>
        <div class="icon-wrapper">
          <i class="fas fa-chevron-down" />
        </div>
      </div>

      <div v-if="isOpen" class="options-container">
        <div class="placeholder-container" @click="handleOptionSelected('')">
          <div class="placeholder-text">{{ placeholder }}</div>
          <div class="icon-wrapper">
            <i class="fas fa-chevron-up" />
          </div>
        </div>
        <div
          class="option"
          v-for="option in options"
          v-bind:key="option"
          @click="handleOptionSelected(option.key)"
        >
          {{ option.text }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, PropType, watchEffect } from "vue";

type SelectInputOption = {
  key: string;
  text: string;
};

interface Props {
  options: SelectInputOption[];
  value: string;
  placeholder: string;
  fill: boolean;
  disabled: boolean;
}

export default defineComponent({
  name: "SelectInpunt",
  props: {
    options: {
      type: Array as PropType<SelectInputOption[]>,
      default() {
        return [];
      },
    },
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    fill: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:value"],
  setup(props: Props) {
    const isOpen = ref<boolean>(false);
    const selectedValue = ref<string>("");

    watchEffect(() => {
      let selected = props?.options?.find((e) => e.key === props.value)?.text;
      selectedValue.value = selected !== undefined ? selected : "";
    });

    return {
      isOpen,
      selectedValue,
    };
  },
  methods: {
    handleOptionSelected(newValue: string) {
      this.$emit("update:value", newValue);
      this.isOpen = false;
    },
    onClick() {
      this.isOpen = true;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
.input-wrapper {
  height: 50px;
  background-color: white;
  border: 1px solid $pj-color-light-gray;
  border-radius: 11px;
  padding: 5px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;

  &:focus-within {
    border: 1px solid $pj-color-black;
  }

  &.error {
    border: 1px solid $pj-color-red;
  }

  &.fill {
    width: 100%;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}
.placeholder-container {
  height: 50px;
  display: flex;
  align-items: center;
  width: 100%;
}
.placeholder-text {
  color: $pj-color-cool-gray;
  font-size: 16px;
  border: none;
  flex-grow: 1;
  background-color: transparent;
}
.selected-option {
  flex-grow: 1;
  color: $pj-color-black;
  font-size: 16px;
}
.icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $pj-color-black;
  width: 20px;
  height: 20px;
  font-size: 14px;

  i {
    margin: 0;
  }
}
.options-container {
  position: absolute;
  top: -1px;
  left: 0;
  width: 100%;
  min-height: 50px;
  max-height: 205px;
  overflow-y: scroll;
  background-color: white;
  border: 1px solid $pj-color-black;
  border-radius: 11px;
  padding: 0 20px;
  z-index: 100;
}
.option {
  padding: 5px 0;
  color: $pj-color-black;
  font-size: 16px;
}
</style>
